import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY, DEFAULT_SEARCH_INPUT_LENGTH, NOTIFICATION_TIME_OUT } from "../../../../constants/app";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { TJobPreferencesFormValues } from "../../../../models/Onboarding/onboarding";
import { MODE_OF_TRANSPORT } from "../../../../models/Onboarding/onboarding-values";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import { getAddressSuggestionsThunk, getNearbySuburbsThunk } from "../../../../store/auth/thunks";
import { AddressSuggestion, TNearbySuburb } from "../../../../store/auth/types";
import { jobPreferencesThunk } from "../../../../store/onboarding/thunk";
import { TSubUrbs } from "../../../../store/onboarding/types";
import { createValidationSchema } from "../../../../utils/createValidationSchema";
import CustomButton from "../../../UI/Buttons/CustomButton";
import LoadingArrow from "../../../UI/Icons/LoadingArrow";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import CheckboxWithIcon from "../../../UI/Inputs/ChecboxWithIcon";
import RangeSelector from "../../../UI/Inputs/RangeSelector";
import TagsInput from "../../../UI/Inputs/TagsInput";
import TextInput from "../../../UI/Inputs/TextInput";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

interface EditJobPreferencesProps {
  handleClose: (value: boolean) => void;
  isMyPreference?: boolean;
}

const EditJobPreferences: React.FC<EditJobPreferencesProps> = ({
  handleClose,
  isMyPreference = false,
}) => {
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  /** Get the values from the state */
  const nearBySuburbs = useSelector((state) => state.auth.nearbySuburbs);
  const jobPreferences = useSelector(
    (state) =>
      state.onboarding.jobpreferences ??
      state.superadmin.reviewCandidateData?.jobpreference?.[0]
  );
  const userpersonaldetails = useSelector(
    (state) =>
      state.onboarding.userpersonaldetails ??
      state.superadmin.reviewCandidateData
  );
  const inProgress = useSelector((state) => state.onboarding.inProgress);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [maxDistance, setMaxDistance] = useState<number>(0);
  const [suburbs, setSuburbs] = useState<TNearbySuburb[]>([]);
  const [newSuburbs, setNewSuburbs] = useState(nearBySuburbs);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState<boolean>(true);
  const [removedSuburbs, setRemovedSuburbs] = useState<string[]>([]);
  const [searchSuburb, setSearchSuburb] = useState("");
  const [showMaxDistanceAlert, setShowMaxDistanceAlert] = useState<boolean>(false);
  const [hasAddressSelected, setHasAddressSelected] = useState<boolean>(false);
  const [addressSuggestions, setAddressSuggestions] = useState<
      AddressSuggestion[]
    >([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");

  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>([]);
  const [isAddingSuburbs, setIsAddingSuburbs] = useState<boolean>(false);
  const [loadedDistance, setLoadedDistance] = useState<number>(jobPreferences?.max_travel_distance || 0);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);
  const modeOfTransports = [
    MODE_OF_TRANSPORT.CAR,
    MODE_OF_TRANSPORT.BUS,
    MODE_OF_TRANSPORT.TRAIN,
    MODE_OF_TRANSPORT.TRAM,
  ];
  const [selectedModeOfTransport, setSelectedModeOfTransport] = useState<
    string[]
  >([]);

  /**
     * Debounces the `selectedKeyword` state to avoid excessive API calls while the user is typing.
     * The debounced value is used to trigger the API call for fetching candidates.
    */
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  /**
     * Handles the change in the search input by updating the `selectedKeyword` state.
     * It also resets the current page to 1 if the search input is empty or exceeds the default length.
     *
     */
    useEffect(() => {
      if (
        debouncedSearch.length === 0 ||
        debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
      ) {
        setSelectedKeyword(debouncedSearch);
      }
    }, [debouncedSearch]);

  /** Update the values in the array for checkbox of modes of transport */
  const handleModeOfTransport = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const value = e.target.value;
    setSelectedModeOfTransport(
      checked
        ? [...selectedModeOfTransport, value]
        : selectedModeOfTransport.filter((item) => item !== value)
    );
  };

  const handleResetState = () => {
    setSuburbs([])
    setNewSuburbs([])
    setSelectedSuburbs([])
    setRemovedSuburbs([])
  }

  const { handleSubmit, values, errors, setFieldValue, touched  } = useFormik({
    validationSchema: createValidationSchema("jobPreferences"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      max_travel_distance: jobPreferences?.max_travel_distance || 0,
      suburbs: selectedSuburbs || [],
      preferred_transport_modes: selectedModeOfTransport || [],
      address: userpersonaldetails?.address || "",
    },
    onSubmit: async (values: TJobPreferencesFormValues) => {
      const payload = { ...values };
      try {
        const result = await dispatch(jobPreferencesThunk(payload));
        if (isFulfilled(result)) {
          handleResetState();
          dispatch(showNotificationAction("Saved"));
          handleClose(false);
          setTimeout(() => {
            globalThis.location.pathname.includes("candidate/jobs/listings") && globalThis.location.reload();
          }, NOTIFICATION_TIME_OUT);
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
    },
  });

  const fetchNearbySuburbs = async (
    maxDistance: number,
    searchText?: string
  ) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getNearbySuburbsThunk({
        address: userpersonaldetails?.address as string,
        radius: maxDistance,
        ...(searchText ? { search: searchText } : {}),
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    if (jobPreferences && jobPreferences !== null) {
      setMaxDistance(jobPreferences.max_travel_distance);
      setLoadedDistance(jobPreferences.max_travel_distance);

      setSelectedModeOfTransport(
        jobPreferences.preferred_transport_modes !== null
          ? JSON.parse(jobPreferences.preferred_transport_modes)
          : []
      );
      
      // Set loading to false after initial data is loaded
      setIsLoadingSuburbs(false);
      // Ensure button is disabled on initial load
      setIsDisabled(true);
      // Mark initial load as complete
      setInitialLoadComplete(true);
    }
  }, [jobPreferences, setMaxDistance]);

  /** Check and update the values */
  useEffect(() => {
    // if (maxDistance !== 0) {
      setFieldValue("max_travel_distance", maxDistance);
    // }

    if (selectedSuburbs) {
      setFieldValue("suburbs", selectedSuburbs);
    }

    if (selectedModeOfTransport) {
      setFieldValue("preferred_transport_modes", selectedModeOfTransport);
    }
  }, [setFieldValue, maxDistance, selectedSuburbs, selectedModeOfTransport]);

  useEffect(() => {
    setNewSuburbs(nearBySuburbs);

    // 1. Transform saved jobPreferences suburbs
    const input = jobPreferences?.jobpreferencesubrubs ?? [];

    const transformed: TNearbySuburb[] = input.map((suburb: TSubUrbs, index: number) => {
      const isManual = suburb.entre_type === "M" || suburb.suburb.endsWith("- M");

      // Remove any trailing "- digits" or "- M"
      const cleanName = suburb.suburb.replace(/\s*-\s*\d+$/, "").replace(/\s*-\s*M$/, "");

      // Build full label with postcode
      const label = `${cleanName}`;

      return {
        id: 100000 + index,
        suburb: cleanName,
        postcode: suburb.pincode,
        lat: 0,
        lng: 0,
        address: "",
        label: label,
        search_type: isManual ? "manual" : (suburb.entre_type ?? ""),
      };
    });

    const newSuburbKeys = new Set(
      (newSuburbs ?? []).map((suburb) => `${suburb.label} - ${suburb.postcode}`)
    );

    // Keep only transformed suburbs that exist in newSuburbs
    // BUT always keep manual (- M) suburbs
    const filteredTransformed = transformed.filter((suburb) => {
      if (suburb.label.endsWith(" - M") || suburb.search_type === "manual") {
        return true; // always keep manual suburbs
      }
      return newSuburbKeys.has(`${suburb.label} - ${suburb.postcode}`);
    });

    // 2. Merge API suburbs + transformed
    // let allSuburbs: TNearbySuburb[] = [...(newSuburbs ?? []), ...filteredTransformed];

    // 2. Merge API suburbs + transformed + selectedSuburbs
    let allSuburbs: TNearbySuburb[] = [];
    if(searchSuburb !== '') {
      const selectedSuburbObjects: TNearbySuburb[] = selectedSuburbs.map((s, index) => {
        const isManual = s.endsWith(" - M");
        const cleaned = s.replace(" - M", "");
        const [label, postcode] = cleaned.split(" - ");

        return {
          id: 200000 + index, // use a different range to avoid collision
          suburb: label,
          postcode: postcode ?? "",
          lat: 0,
          lng: 0,
          address: "",
          label: label,
          search_type: isManual ? "manual" : "auto",
        };
      });

      allSuburbs = [
        ...(newSuburbs ?? []),
        ...filteredTransformed,
        ...selectedSuburbObjects,
      ];
    } 
    else {
      allSuburbs = [...(newSuburbs ?? []), ...filteredTransformed];
    }

    // 3. Deduplicate by label+postcode
    const uniqueMap = new Map<string, TNearbySuburb>();
    allSuburbs.forEach((s) => {
      const key = `${s.label} - ${s.postcode}`;
      if (!uniqueMap.has(key)) uniqueMap.set(key, s);
    });

    const deduplicatedSuburbs = Array.from(uniqueMap.values());

    // 4. Update suburbs list (for dropdown)
    // setSuburbs(deduplicatedSuburbs);
    
    // 4. Update suburbs list (for dropdown)
    const isManualSearch = Boolean(searchSuburb);

    const selectedSuburbKeys = new Set(
      selectedSuburbs.map((s) => s.replace(" - M", ""))
    );

    const removedSuburbKeys = new Set(
      removedSuburbs.map((s) => s.replace(" - M", ""))
    );

    // ✅ During manual search, show all suburbs including selected ones (with checked checkboxes)
    // Only exclude suburbs that user explicitly removed
    const filteredDropdownSuburbs = isManualSearch
      ? deduplicatedSuburbs.filter((suburb) => {
          const key = `${suburb.label} - ${suburb.postcode}`;
          return !removedSuburbKeys.has(key);
        })
      : deduplicatedSuburbs;

    setSuburbs(filteredDropdownSuburbs);

    // 5. Update selectedSuburbs
    if (searchSuburb) {
      // If this was a manual search, don’t auto‑select anything
      return;
    }

    // 6. If maxDistance changed or initial load:
    setSelectedSuburbs((prev) => {
      // Keep manual (- M) selections
      const manualSelections = prev.filter((s) => s.endsWith('- M'));

      const savedSelections = transformed.map(
        (s) =>
          `${s.label}${s.postcode ? ` - ${s.postcode}` : ""}${
            s.search_type === "manual" ? " - M" : ""
          }`
      );

      const savedKeys = new Set(
        transformed.map(
          (s) => `${s.label}${s.postcode ? ` - ${s.postcode}` : ""}`
        )
      );

      // Auto‑select all API suburbs
      if (isFirstLoad.current) {
        // ✅ Initial load: only keep manual + saved ones
        isFirstLoad.current = false;

        if((jobPreferences !== null && jobPreferences?.max_travel_distance) && jobPreferences?.max_travel_distance > 0) {
          fetchNearbySuburbs(jobPreferences?.max_travel_distance)
        }

        const filteredSavedSelections = savedSelections.filter(
          (s) => !removedSuburbs.includes(s.replace(" - M", ""))
        );

        return Array.from(new Set([...filteredSavedSelections]));
      } else {
        // Get all non-manual suburbs from API response
        let autoSelections: any = deduplicatedSuburbs.filter((s) => s.search_type !== "manual");

        // When distance changed (reload happened), show ALL new suburbs from API
        // Don't filter by savedKeys unless it's the same distance as original
        const isOriginalDistance = maxDistance === jobPreferences?.max_travel_distance;
        const hasReloaded = maxDistance === loadedDistance;
        
        if (savedKeys.size > 0 && isOriginalDistance && hasReloaded && !isAddingSuburbs) {
          // Only filter by saved suburbs if we're at original distance and haven't manually added suburbs
          autoSelections = autoSelections.filter((s: any) =>
            savedKeys.has(`${s.label} - ${s.postcode}`)
          );
        }

        // Format auto-selections and filter out duplicates with manual selections
        autoSelections = autoSelections
          .map((s: any) => `${s.label} - ${s.postcode}`)
          .filter(
            (auto: any) =>
              !manualSelections.some((manual) =>
                auto.startsWith(manual.replace(" - M", ""))
              )
          )
          .filter((auto: any) => !removedSuburbs.includes(auto));
        
        // Always preserve manual selections + auto selections based on current distance
        if(!isAddingSuburbs && isOriginalDistance) {
          return Array.from(
            new Set([...manualSelections, ...savedSelections, ...autoSelections])
          );
        } else {
          // When distance changed or adding suburbs manually, only keep manual + new API suburbs
          return Array.from(
            new Set([...manualSelections, ...autoSelections])
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearBySuburbs, jobPreferences, newSuburbs, searchSuburb]);

  useEffect(() => {
    if(selectedSuburbs.length > 0) {
      const selectedSuburbObjects: TNearbySuburb[] = selectedSuburbs.map((s, index) => {
        const isManual = s.endsWith(" - M");
        const cleaned = s.replace(" - M", "");
        const [label, postcode] = cleaned.split(" - ");

        return {
          id: 200000 + index, // use a different range to avoid collision
          suburb: label,
          postcode: postcode ?? "",
          lat: 0,
          lng: 0,
          address: "",
          label: label,
          search_type: isManual ? "manual" : "auto",
        };
      });

      const selectedSuburbsList = [
        ...(newSuburbs ?? []),
        ...selectedSuburbObjects,
      ];

      const uniqueMap = new Map<string, TNearbySuburb>();
      selectedSuburbsList.forEach((s) => {
        const key = `${s.label} - ${s.postcode}`;
        if (!uniqueMap.has(key)) uniqueMap.set(key, s);
      });

      const deduplicatedSuburbs = Array.from(uniqueMap.values());

      const isManualSearch = Boolean(searchSuburb);

      const selectedSuburbKeys = new Set(
        selectedSuburbs.map((s) => s.replace(" - M", ""))
      );

      const removedSuburbKeys = new Set(
        removedSuburbs.map((s) => s.replace(" - M", ""))
      );

      // ✅ During manual search, show all suburbs including selected ones (with checked checkboxes)
      // Only exclude suburbs that user explicitly removed
      const filteredDropdownSuburbs = isManualSearch
        ? deduplicatedSuburbs.filter((suburb) => {
            const key = `${suburb.label} - ${suburb.postcode}`;
            return !removedSuburbKeys.has(key);
          })
        : deduplicatedSuburbs;

      setSuburbs(filteredDropdownSuburbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuburbs, newSuburbs, searchSuburb]);

  useEffect(() => {
    // If distance is zero, don't auto-populate suburbs
    if (maxDistance === 0 && selectedSuburbs.length > 0) {
      setSuburbs([]);
      setSelectedSuburbs([]);
      setHasLoadedOnce(false);
      // Also reset loadedDistance so the user can reload suburbs
      // after they change the distance back from 0.
      setLoadedDistance(0);
      return;
    }
  }, [maxDistance, selectedSuburbs]);

  const handleSuburbsChange = (tags: string | Array<string>) => {
    if (!Array.isArray(tags)) return;

    const newSelected = tags.map((label) => label);
    const removed = selectedSuburbs.filter((s) => !newSelected.includes(s));
    const reAdded = removedSuburbs.filter((s) => {
      // Check if this removed suburb is being re-added (with or without " - M")
      return newSelected.some(sel => 
        sel.replace(" - M", "") === s.replace(" - M", "")
      );
    });

    setSelectedSuburbs(newSelected);

    // Only update removedSuburbs if NOT in search mode
    // During search, unchecking a checkbox should NOT hide the item from dropdown
    // Only clicking X button on tags (when not searching) should hide items
    if (!searchSuburb) {
      setRemovedSuburbs((prev) => {
        const updated = [...prev, ...removed];
        const final = updated.filter((suburb) => {
          // Remove from removedSuburbs if it was re-added
          return !reAdded.some(readd => 
            readd.replace(" - M", "") === suburb.replace(" - M", "")
          );
        });
        return Array.from(new Set(final));
      });
    } else {
      // During search, only clear re-added items from removedSuburbs
      if (reAdded.length > 0) {
        setRemovedSuburbs((prev) => {
          return prev.filter((suburb) => {
            return !reAdded.some(readd => 
              readd.replace(" - M", "") === suburb.replace(" - M", "")
            );
          });
        });
      }
    }
  };

  const handleReloadSuburbs = async () => {
    setIsLoadingSuburbs(true);
    setHasLoadedOnce(true);
    setSearchSuburb("");
    await fetchNearbySuburbs(maxDistance).then(() => {
      setHasLoadedOnce(true);
      setIsAddingSuburbs(false);
      setLoadedDistance(maxDistance);
      setIsLoadingSuburbs(false);
    });
  };

  const handleSearch = async (searchText: string) => {
    if (!userpersonaldetails?.address || maxDistance < 1) return;
    setIsLoadingSuburbs(true);
    setHasLoadedOnce(true);
    setSearchSuburb(searchText);
    await fetchNearbySuburbs(maxDistance, searchText).then(() => {
      setHasLoadedOnce(true);
      setIsLoadingSuburbs(false);
    });
  };



  /** Check all the values got updated for enabling the next button */
  useEffect(() => {
    // Don't run comparison until initial data is loaded
    if (!initialLoadComplete) {
      return;
    }

    const originalAddress = userpersonaldetails?.address || "";
    const currentAddress = values.address || "";
    const hasAddressChanged = currentAddress !== originalAddress && currentAddress.trim().length > 0;
    
    // Check if any values have changed from the original
    const originalDistance = jobPreferences?.max_travel_distance || 0;
    const hasDistanceChanged = values.max_travel_distance !== originalDistance;
    
    const originalTransport = jobPreferences?.preferred_transport_modes 
      ? JSON.parse(jobPreferences.preferred_transport_modes) 
      : [];
    const hasTransportChanged = JSON.stringify(values.preferred_transport_modes.sort()) !== JSON.stringify(originalTransport.sort());
    
    // Normalize suburbs for comparison (remove " - M" suffix for manual entries)
    const normalizeSuburb = (suburb: string) => suburb.replace(/\s*-\s*M$/, "");
    
    const originalSuburbs = jobPreferences?.jobpreferencesubrubs?.map((s: TSubUrbs) => 
      `${s.suburb.replace(/\s*-\s*\d+$/, "").replace(/\s*-\s*M$/, "")} - ${s.pincode}`
    ) || [];
    
    const normalizedCurrentSuburbs = values.suburbs.map(normalizeSuburb).sort();
    const normalizedOriginalSuburbs = originalSuburbs.map(normalizeSuburb).sort();
    
    const hasSuburbsChanged = JSON.stringify(normalizedCurrentSuburbs) !== JSON.stringify(normalizedOriginalSuburbs);
    
    // Check if something has changed
    const hasAnythingChanged = hasAddressChanged || hasDistanceChanged || hasTransportChanged || hasSuburbsChanged;
    
    // Validate that required fields have values when changes exist
    const hasValidRequiredFields = 
      values.max_travel_distance > 0 &&
      values.preferred_transport_modes.length > 0 &&
      values.suburbs.length > 0;
    
    // Enable button only if something changed AND all required fields are valid
    if (hasAnythingChanged && hasValidRequiredFields) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [values, userpersonaldetails?.address, jobPreferences, initialLoadComplete]);

  const handleAddressChange = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
    const inputValue = e.target.value;
    setHasAddressSelected(false);
    setFieldValue("address", inputValue);
    if (inputValue.length > 2 && !hasAddressSelected) {
      handleAddressSuggestions(inputValue);
    }
  };

  const handleAddressSuggestions = async (selectedKeyword: string) => {
    const response = await dispatch(
      getAddressSuggestionsThunk(selectedKeyword)
    );

    if (isFulfilled(response)) {
      setAddressSuggestions(response.payload.data);
    } else if (searchInput.length === 0) {
      setAddressSuggestions([]);
    } else {
      setAddressSuggestions([]);
    } 
  };

   const handleSelectAddress = (selectedAddress: string) => {
    setFieldValue("address", selectedAddress);
    setAddressSuggestions([]); // Hide dropdown after selection
    setSelectedKeyword("");
    setHasAddressSelected(true);
  };

  return (
    <div className="d-flex flex-column gap-15 fw-400 text-black">
      <div className="fs-22">{isMyPreference ? "Location Preferences" : "Edit Job Preferences"}</div>
      <div className="w-100 h-1px"></div>      
      { /**
       * Displayed only in the job listing page "My Preference" edit form
       */
        isMyPreference && (
        <div className="position-relative d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            Set your home address
          </div>
          <TextInput
            value={values.address || ""}
            field={"address"}
            label={"Home Address"}
            onChange={handleAddressChange}
            touched={touched.address}
            error={errors.address}
            placeholder={" " + ""}
          />
          {/* Address Suggestions Dropdown */}
          {addressSuggestions.length > 0 ? (
            <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100">
              {addressSuggestions.map((addr, index) => (
                <div
                  key={index}
                  className="px-15px py-8px d-flex align-items-center cursor-pointer hover-bg-off-white text-break"
                  onClick={() => handleSelectAddress(addr.description)}
                >
                  {addr.description}
                </div>
              ))}
            </div>
          ) : selectedKeyword.length > 0 &&
            addressSuggestions.length === 0 ? (
            <div className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden w-100 py-10px pl-10px">
              No Address Found
            </div>
          ) : (
            ""
          )}
        </div>
        )
      }
     

      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black">
          Set your maximum travel distance from home:
        </div>
        <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center">
          <RangeSelector
            min={0}
            max={100}
            setMaxDistance={setMaxDistance}
            setIsLoadingSuburbs={setIsLoadingSuburbs}
            setShowMaxDistanceAlert={setShowMaxDistanceAlert}
            rangeType="haveIcon"
          />
          <CustomButton
            variant={"primary"}
            onClick={handleReloadSuburbs}
            disabled={isLoadingSuburbs || maxDistance === loadedDistance}
            className="btn-theme bg-teal text-black min-w-126px mobile-w-100"
          >
            {!inProgress ? <LoadingArrow /> : ""}
            Reload Suburbs
          </CustomButton>
        </div>
        {showMaxDistanceAlert && <ErrorMessage className="align-items-center gap-3 lh-1px" message="Maximum distance cannot exceed 100km." />}
      </div>
      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black user-select-none">
          Remove or add suburbs to your preferred job locations:
        </div>
        <TagsInput
          onSelectionChange={handleSuburbsChange}
          selectedItems={selectedSuburbs}
          items={suburbs}
          onSearch={handleSearch}
          isSuburb={true}
        />
        {errors && <ErrorMessage message={errors.suburbs as string} />}
      </div>
      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black">
          Select you preferred mode/s of transport:
        </div>
        <div className="d-flex gap-10 modes-of-trensport-container">
          {modeOfTransports.map((modeOfTransport: string) => {
            const transportsLc = modeOfTransport.toLowerCase();
            return (
              <div key={uuidv4()} className="mobile-w-50">
                <CheckboxWithIcon
                  id={`transport_${transportsLc}`}
                  value={modeOfTransport}
                  checked={selectedModeOfTransport.includes(modeOfTransport)}
                  label={modeOfTransport}
                  onChange={handleModeOfTransport}
                  iconClass={`icon-${transportsLc}`}
                />
              </div>
            );
          })}
        </div>
        {errors && (
          <ErrorMessage message={errors.preferred_transport_modes as string} />
        )}
      </div>
      <div className="d-flex justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          disabled={isDisabled || isLoadingSuburbs}
          onClick={handleSubmit}
          isLoading={inProgress}
          contentClass="d-flex"
        >
          <RoundedTick color={(inProgress || isDisabled || isLoadingSuburbs) ? "#aaaaaa" : "black"} />
          Save
        </CustomButton>
      </div>
    </div>
  );
};

export default EditJobPreferences;
